import { useAccount } from "@azure/msal-react";
import {
    ActionIcon,
    AppShell,
    AppShellMain,
    Box,
    Button,
    rem,
    ScrollArea,
    Text,
    TextInput,
    Tooltip,
    useMantineTheme,
} from "@mantine/core";
import * as Sentry from "@sentry/react";
import {
    IconBug,
    IconKey,
    IconLayoutSidebarLeftCollapse,
    IconLayoutSidebarLeftExpand,
    IconLock,
    IconMessage2Plus,
    IconSearch,
    IconSettings,
    IconX,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useLiveQuery } from "dexie-react-hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChatRouteChild, type ContextTag } from "src/containers/ChatRoute";
import { useClearForbiddenFlag } from "src/hooks/useClearForbiddenFlag";
import { useMetadata } from "src/hooks/useMetadata";
import { useNotAuthorizedUser } from "src/utils/useAdmin";
import { db } from "../db";
import { useLoadChatHistory } from "../hooks/useLoadChatHistory";
import { Chats } from "./Chats";
import { TermsOfUseModal, useTermsOfUse } from "./TermsOfUseModal";

/** Context passed from the host application */
export interface CopilotContext {
    // Equipment identifiers
    equipmentId?: string;
    equipmentName?: string;
    equipmentType?: string;
    productFamily?: string;
    trainName?: string;
    location?: string;

    // Deviation/Alert info
    deviationTitle?: string;
    alertDescription?: string;
    severity?: string | number;
    createdDate?: string;
    lastOccurred?: string;

    // Generic metadata and suggested question
    metadata?: Record<string, string>;
    suggestedQuestion?: string;
}

/**
 * CopilotLayout - A compact layout for embedding the full CTS KS app as a copilot sidebar
 *
 * Features:
 * - No header (for cleaner embedded experience)
 * - Collapsible chat history sidebar (starts collapsed by default)
 * - Compact styling optimized for sidebar/docked usage
 * - Resizable from parent container
 * - postMessage API for parent communication
 *
 * Note: Authentication is handled by CopilotAuthProvider which wraps this component
 */
export function CopilotLayout() {
    const theme = useMantineTheme();
    const queryClient = useQueryClient();
    const isNotAuthorized = useNotAuthorizedUser();
    useClearForbiddenFlag();
    const { termsModalOpened, closeTermsModal } = useTermsOfUse();
    const { resetChatMetadata, addChatMetadata } = useMetadata();
    const [search, setSearch] = useState("");
    // Track active chat ID - null means new chat
    const [activeChatId, setActiveChatId] = useState<string | null>(null);

    const account = useAccount();

    // Sidebar is collapsed by default in copilot mode
    const [hideHistoryBar, setHideHistoryBar] = useState(true);

    // Context from host application
    const [context, setContext] = useState<CopilotContext | null>(null);
    // Suggested question to pre-fill
    const [suggestedQuestion, setSuggestedQuestion] = useState<string>("");

    // Apply context to chat metadata
    const applyContext = useCallback(
        (ctx: CopilotContext) => {
            // Normalize kebab-case keys to camelCase for backward compatibility
            const normalized: CopilotContext = {};
            for (const [key, value] of Object.entries(ctx)) {
                const camelKey = key.replace(/-([a-z])/g, (_, c) =>
                    c.toUpperCase(),
                ) as keyof CopilotContext;
                (normalized as Record<string, unknown>)[camelKey] = value;
            }

            setContext(normalized);

            // Map context fields to metadata
            if (normalized.equipmentId) {
                addChatMetadata(normalized.equipmentId, "machine:");
            }
            if (normalized.equipmentType) {
                addChatMetadata(normalized.equipmentType, "product:");
            }
            if (normalized.location) {
                addChatMetadata(normalized.location, "location:");
            }
            // Apply any additional metadata - allow ALL fields
            if (normalized.metadata) {
                for (const [key, value] of Object.entries(normalized.metadata)) {
                    addChatMetadata(value, `${key}:`);
                }
            }

            // Also add top-level unknown fields to metadata
            const knownFields = [
                "equipmentId",
                "equipmentType",
                "alertDescription",
                "location",
                "metadata",
                "suggestedQuestion",
            ];
            for (const [key, value] of Object.entries(normalized)) {
                if (!knownFields.includes(key) && typeof value === "string") {
                    addChatMetadata(value, `${key}:`);
                }
            }

            // Set suggested question if provided
            if (normalized.suggestedQuestion) {
                setSuggestedQuestion(normalized.suggestedQuestion);
            }

            console.info("[Copilot] Context applied:", normalized);
        },
        [addChatMetadata],
    );

    // Clear all context
    const clearContext = useCallback(() => {
        setContext(null);
        setSuggestedQuestion("");
        resetChatMetadata();
        console.info("[Copilot] Context cleared");
    }, [resetChatMetadata]);

    // Remove a specific context tag by label
    const removeContextTag = useCallback(
        (label: string) => {
            if (!context) return;

            // Create a copy of the context and remove the relevant field
            const updatedContext = { ...context };
            const labelLower = label.toLowerCase();

            // Map labels back to context fields
            if (labelLower === "deviation") {
                delete updatedContext.deviationTitle;
            } else if (labelLower === "severity") {
                delete updatedContext.severity;
            } else if (labelLower === "equipment") {
                delete updatedContext.equipmentId;
                delete updatedContext.equipmentName;
            } else if (labelLower === "train") {
                delete updatedContext.trainName;
            } else if (labelLower === "product") {
                delete updatedContext.productFamily;
            } else if (labelLower === "type") {
                delete updatedContext.equipmentType;
            } else if (labelLower === "location") {
                delete updatedContext.location;
            } else if (labelLower === "last occurred") {
                delete updatedContext.lastOccurred;
            } else if (labelLower === "created") {
                delete updatedContext.createdDate;
            } else if (labelLower === "alert") {
                delete updatedContext.alertDescription;
            }

            // Check if context is now empty
            const hasRemainingContext = Object.values(updatedContext).some(
                (v) => v !== undefined && v !== null && v !== "",
            );

            if (hasRemainingContext) {
                setContext(updatedContext);
                console.info("[Copilot] Context tag removed:", label);
            } else {
                // If no context remains, clear everything
                clearContext();
            }
        },
        [context, clearContext],
    );

    // Listen for history updates from widget via BroadcastChannel
    useEffect(() => {
        let historyChannel: BroadcastChannel | null = null;

        try {
            historyChannel = new BroadcastChannel("cts-chat-history");

            historyChannel.onmessage = (event) => {
                const { type, chatId, messageId } = event.data;
                console.info("[Copilot] Received history update:", type, { chatId, messageId });

                if (type === "chat-created" || type === "message-added") {
                    db.chats.count().then(() => {
                        console.info("[Copilot] Chat history refreshed");
                    });
                }
            };

            console.info("[Copilot] BroadcastChannel listener initialized for history sync");
        } catch (error) {
            console.warn("[Copilot] BroadcastChannel not supported:", error);
        }

        return () => {
            if (historyChannel) {
                historyChannel.close();
            }
        };
    }, []);

    // Load chat history from backend (same as main app)
    // isHistoryLoading prevents sending messages until history sync completes
    // This avoids a race condition where new local chats get wiped by the sync
    const { isLoading: isHistoryLoading } = useLoadChatHistory();

    const chats = useLiveQuery(() => db.chats.orderBy("createdAt").reverse().toArray());

    // Get messages for active chat
    const messages = useLiveQuery(() => {
        if (!activeChatId) return [];
        return db.messages.where("chatId").equals(activeChatId).sortBy("createdAt");
    }, [activeChatId]);

    // Note: We intentionally do NOT auto-select a chat on page load.
    // The widget should start with a fresh conversation.
    // Users can select a previous chat from the sidebar if needed.

    // Send ready message to parent when copilot is ready
    useEffect(() => {
        if (account && window.parent !== window) {
            window.parent.postMessage({ type: "cts-copilot-ready" }, "*");
        }
    }, [account]);

    // Auto-focus the chat input once history loading finishes.
    // When the copilot is embedded in an iframe the auth popup returns focus
    // to the host page rather than the iframe. window.focus() (called inside
    // CopilotAuthProvider after the popup closes) regains iframe-level focus
    // but doesn't focus any specific element. We explicitly focus the textarea
    // here so the user can type immediately without an extra click.
    useEffect(() => {
        if (!isHistoryLoading && window.parent !== window) {
            const timer = setTimeout(() => {
                const input = document.getElementById("chat-input") as HTMLTextAreaElement | null;
                input?.focus();
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [isHistoryLoading]);

    // Listen for messages from parent
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data?.type === "cts-copilot-navigate") {
                // Handle navigation requests from parent
                const { chatId } = event.data;
                if (chatId) {
                    setActiveChatId(chatId);
                }
            } else if (event.data?.type === "cts-copilot-new-chat") {
                // Handle new chat request
                clearContext();
                setActiveChatId(null);
            } else if (event.data?.type === "cts-copilot-toggle-sidebar") {
                // Handle sidebar toggle request
                setHideHistoryBar((prev) => !prev);
            } else if (event.data?.type === "cts-copilot-set-context") {
                // Handle context from host app
                const ctx = event.data.context as CopilotContext;
                if (ctx) {
                    applyContext(ctx);
                }
            } else if (event.data?.type === "cts-copilot-clear-context") {
                // Handle clear context request
                clearContext();
            } else if (event.data?.type === "cts-copilot-ask") {
                // Handle programmatic question with optional context
                const { question, context: ctx } = event.data;
                if (ctx) {
                    applyContext(ctx);
                }
                if (question) {
                    setSuggestedQuestion(question);
                }
            } else if (event.data?.type === "cts-copilot-open-settings") {
                // Handle settings/preferences open request - open questionnaire in new tab
                window.open("/#/questionnaire", "_blank");
            } else if (event.data?.type === "cts-preferences-updated") {
                // Questionnaire (opened as new tab) notified us that preferences were saved
                queryClient.invalidateQueries({ queryKey: ["user"] });
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [applyContext, clearContext, queryClient]);

    // Handle chat selection from sidebar
    const handleChatSelect = (chatId: string) => {
        setActiveChatId(chatId);
        setHideHistoryBar(true);
    };

    // Handle new chat creation
    const handleNewChat = () => {
        clearContext();
        setActiveChatId(null);
    };

    // Handle when a new chat is created (from sending first message)
    const handleChatCreated = (chatId: string) => {
        setActiveChatId(chatId);
    };

    // Store suggestedQuestion in ref for stable callback reference
    // This prevents infinite re-renders when suggestedQuestion changes
    const suggestedQuestionRef = useRef(suggestedQuestion);
    useEffect(() => {
        suggestedQuestionRef.current = suggestedQuestion;
    }, [suggestedQuestion]);

    // Clear suggested question after it's been used (stable callback)
    const handleContentChange = useCallback((content: string) => {
        // If the suggested question was consumed (content changed from it), clear it
        if (suggestedQuestionRef.current && content !== suggestedQuestionRef.current) {
            setSuggestedQuestion("");
        }
    }, []);

    // Build context tags from the current context
    const contextTags = useMemo<ContextTag[]>(() => {
        if (!context) return [];

        const tags: ContextTag[] = [];

        // Deviation/Alert title (most prominent)
        if (context.deviationTitle) {
            tags.push({
                label: "Deviation",
                value:
                    context.deviationTitle.length > 40
                        ? `${context.deviationTitle.substring(0, 40)}...`
                        : context.deviationTitle,
                type: "alert",
            });
        }

        // Severity
        if (context.severity !== undefined && context.severity !== null) {
            tags.push({
                label: "Severity",
                value: String(context.severity),
                type: "alert",
            });
        }

        // Equipment info
        if (context.equipmentName) {
            tags.push({
                label: "Equipment",
                value: context.equipmentName,
                type: "equipment",
            });
        } else if (context.equipmentId) {
            tags.push({
                label: "Equipment",
                value: context.equipmentId,
                type: "equipment",
            });
        }

        // Train name
        if (context.trainName) {
            tags.push({
                label: "Train",
                value: context.trainName,
                type: "equipment",
            });
        }

        // Product family / type
        if (context.productFamily) {
            tags.push({
                label: "Product",
                value: context.productFamily,
                type: "default",
            });
        } else if (context.equipmentType) {
            tags.push({
                label: "Type",
                value: context.equipmentType,
                type: "default",
            });
        }

        // Location
        if (context.location) {
            tags.push({
                label: "Location",
                value: context.location,
                type: "location",
            });
        }

        // Dates (show last occurred if available, otherwise created date)
        if (context.lastOccurred) {
            tags.push({
                label: "Last Occurred",
                value: context.lastOccurred,
                type: "default",
            });
        } else if (context.createdDate) {
            tags.push({
                label: "Created",
                value: context.createdDate,
                type: "default",
            });
        }

        // Alert description (legacy field)
        if (context.alertDescription && !context.deviationTitle) {
            tags.push({
                label: "Alert",
                value:
                    context.alertDescription.length > 50
                        ? `${context.alertDescription.substring(0, 50)}...`
                        : context.alertDescription,
                type: "alert",
            });
        }

        return tags;
    }, [context]);

    // Auth is handled by CopilotAuthProvider - this component only renders when authenticated

    // If user has no data source access, show request access UI
    if (isNotAuthorized) {
        return (
            <Box
                style={{
                    height: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "24px",
                    textAlign: "center",
                }}
            >
                <IconLock size={48} color={theme.colors.gray[5]} />
                <Text size="lg" fw={600} mt="md">
                    Access Required
                </Text>
                <Text size="sm" c="dimmed" mt="xs" maw={300}>
                    You don't have access to any data sources yet. Request access to start using CTS
                    Knowledge Search.
                </Text>
                <Button
                    mt="lg"
                    leftSection={<IconKey size={16} />}
                    onClick={() => window.open("/#/unauthorized", "_blank")}
                >
                    Request Access
                </Button>
            </Box>
        );
    }

    return (
        <AppShell
            layout="default"
            padding={0}
            navbar={{
                width: { base: 200, lg: 250 },
                breakpoint: "xs",
                collapsed: {
                    mobile: hideHistoryBar,
                    desktop: hideHistoryBar,
                },
            }}
        >
            {/* Compact Navbar */}
            <AppShell.Navbar
                style={{
                    height: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    borderRight: `1px solid ${theme.colors.gray[2]}`,
                    backgroundColor: theme.white,
                }}
            >
                {/* Header area with toggle and new chat */}
                <Box
                    style={{
                        padding: "8px",
                        borderBottom: `1px solid ${theme.colors.gray[1]}`,
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                    }}
                >
                    <Tooltip label="Collapse sidebar" position="right">
                        <ActionIcon
                            variant="subtle"
                            onClick={() => setHideHistoryBar(true)}
                            size="sm"
                        >
                            <IconLayoutSidebarLeftCollapse size={18} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="New Chat" position="right">
                        <ActionIcon
                            variant="filled"
                            color="purple"
                            onClick={handleNewChat}
                            size="sm"
                            style={{ flex: 1 }}
                        >
                            <IconMessage2Plus size={16} />
                        </ActionIcon>
                    </Tooltip>
                </Box>

                {/* Search */}
                <TextInput
                    variant="default"
                    placeholder="Search"
                    value={search}
                    onChange={(event) => setSearch(event.currentTarget.value.toLowerCase())}
                    style={{ padding: `${rem(4)} ${rem(8)}` }}
                    leftSection={<IconSearch opacity={0.8} size={16} />}
                    rightSection={
                        !!search && (
                            <ActionIcon aria-label="Clear" onClick={() => setSearch("")} size="sm">
                                <IconX opacity={0.5} size={14} />
                            </ActionIcon>
                        )
                    }
                    size="xs"
                />

                {/* Chat history */}
                <Box
                    component={ScrollArea}
                    style={{
                        flex: 1,
                        position: "relative",
                    }}
                >
                    <Chats chats={chats} search={search} onChatSelect={handleChatSelect} />
                </Box>

                {/* Preferences */}
                <Box
                    style={{
                        padding: "8px",
                        borderTop: `1px solid ${theme.colors.gray[1]}`,
                        display: "flex",
                        justifyContent: "flex-end",
                    }}
                >
                    <Tooltip label="Preferences" position="top">
                        <ActionIcon
                            size="sm"
                            variant="subtle"
                            aria-label="Preferences"
                            onClick={() => window.open("/#/questionnaire", "_blank")}
                        >
                            <IconSettings size={16} />
                        </ActionIcon>
                    </Tooltip>
                </Box>
            </AppShell.Navbar>

            {/* Main content */}
            <AppShellMain
                style={{
                    height: "100vh",
                    position: "relative",
                }}
            >
                {/* Floating buttons when sidebar is collapsed */}
                {hideHistoryBar && (
                    <Box
                        style={{
                            position: "absolute",
                            top: "8px",
                            left: "8px",
                            zIndex: 100,
                            display: "flex",
                            gap: "4px",
                        }}
                    >
                        <Tooltip label="Show chat history" position="right">
                            <ActionIcon
                                variant="subtle"
                                onClick={() => setHideHistoryBar(false)}
                                size="sm"
                                style={{
                                    backgroundColor: theme.white,
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                }}
                            >
                                <IconLayoutSidebarLeftExpand size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Report a bug" position="right">
                            <ActionIcon
                                variant="subtle"
                                size="sm"
                                style={{
                                    backgroundColor: theme.white,
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                }}
                                onClick={async () => {
                                    const feedback = Sentry.getFeedback();
                                    if (feedback) {
                                        const form = await feedback.createForm();
                                        form.appendToDom();
                                        form.open();
                                    }
                                }}
                            >
                                <IconBug size={18} />
                            </ActionIcon>
                        </Tooltip>
                    </Box>
                )}

                {/* Render ChatRouteChild directly in compact mode without header */}
                <ChatRouteChild
                    chatId={activeChatId ?? undefined}
                    messages={messages}
                    hideHeader
                    isCompact
                    initialContent={suggestedQuestion}
                    onContentChange={handleContentChange}
                    contextTags={contextTags}
                    onClearContext={(label) => {
                        if (label) {
                            removeContextTag(label);
                        } else {
                            clearContext();
                        }
                    }}
                    onChatCreated={handleChatCreated}
                    disabled={isHistoryLoading}
                />
            </AppShellMain>
            <TermsOfUseModal opened={termsModalOpened} onClose={closeTermsModal} />
        </AppShell>
    );
}
